import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react'
import { FiCheckCircle, FiXCircle } from 'react-icons/fi'
import NumberFormat, {
  NumberFormatProps,
  NumberFormatValues
} from 'react-number-format'
import { Spinner } from 'reactstrap'

import { useField } from '@unform/core'

import * as S from '../styles'
import { handleCustomFormat } from './helpers'

export type IInputMaskProps = Omit<NumberFormatProps, 'name'> & InputMaskProps

const InputCreditCard: React.FC<IInputMaskProps> = ({
  name,
  icon: Icon,
  format,
  mask = '•',
  onBlurCustom,
  loading = false,
  showBadgeIsValid = false,
  showBadgeIsInvalid = false,
  disabled = false,
  showWarningOnRequired = true,
  iconRight,
  onBlur,
  maxLength,
  ...rest
}) => {
  const inputRef =
    useRef<NumberFormat<React.InputHTMLAttributes<HTMLInputElement>>>(null)

  const [isFilled, setIsFilled] = useState(false)
  const [valueInput, setValueInput] = useState('')

  const { fieldName, defaultValue, error, registerField, clearError } =
    useField(name)

  const inputChangedHandler = (values: NumberFormatValues) => {
    setValueInput(values.value)
  }

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlurCustom && inputRef.current) {
        onBlurCustom({
          value: inputRef.current.state?.value || '',
          name: inputRef.current.state?.name || fieldName,
          clearError,
          setIsFilled
        })
      }

      onBlur?.(e)

      setIsFilled(!!inputRef.current?.state?.value)
    },
    [clearError, onBlurCustom, onBlur, fieldName]
  )

  useEffect(() => {
    if (inputRef.current) {
      registerField({
        name: fieldName,
        ref: inputRef.current,
        path: 'state.value'
      })
    }
  }, [fieldName, registerField])

  /**
   * Handle initial value
   */
  const defaultValueIsNull = defaultValue === null
  const defaultValueIsUndefined = typeof defaultValue === 'undefined'

  useEffect(() => {
    if (!defaultValueIsNull && !defaultValueIsUndefined) {
      setIsFilled(true)
    }
  }, [defaultValue, defaultValueIsNull, defaultValueIsUndefined])

  /**
   * Custom format
   *
   */
  const customFormat = useMemo(() => {
    const shouldKeepDefaultFormat = maxLength && maxLength < 16 && !!format

    if (shouldKeepDefaultFormat) return { format, mask }

    return handleCustomFormat({ value: valueInput, mask })
  }, [format, mask, maxLength, valueInput])

  return (
    <>
      <S.Container
        isErrored={!!error || showBadgeIsInvalid}
        isDisabled={disabled}
        showWarning={!isFilled && showWarningOnRequired && rest?.required}
      >
        {Icon && (typeof Icon === 'function' ? <Icon /> : Icon)}

        <NumberFormat
          onBlur={handleInputBlur}
          name={fieldName}
          aria-label={fieldName}
          mask={customFormat.mask}
          disabled={disabled}
          isNumericString
          format={customFormat.format}
          onValueChange={inputChangedHandler}
          value={valueInput}
          defaultValue={defaultValue === null ? '' : defaultValue}
          {...rest}
          ref={inputRef}
        />

        {showBadgeIsValid && (
          <S.IconWrapper className="success rightElement">
            <FiCheckCircle />
          </S.IconWrapper>
        )}

        {showBadgeIsInvalid && (
          <S.IconWrapper className="error rightElement">
            <FiXCircle />
          </S.IconWrapper>
        )}

        {loading && (
          <Spinner
            className="rightElement"
            type="grow"
            size="sm"
            color="secondary"
          />
        )}

        {iconRight && (
          <div className="right-icon rightElement">
            {typeof iconRight === 'function'
              ? React.createElement(iconRight)
              : iconRight}
          </div>
        )}
      </S.Container>
      {error && <S.MessageError>{error}</S.MessageError>}
    </>
  )
}

export default InputCreditCard
